import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Same-origin (behind the ingress) → httpOnly cookies are sufficient. We do
// NOT persist tokens in localStorage to reduce XSS blast radius.
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 20000,
});

/* ---------------- Transparent retry on cold-start / transient failures ---------------- */
const RETRY_STATUS = new Set([502, 503, 504]);
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1800;
const NOTIFY_AFTER_ATTEMPT = 1; // show the friendly banner from the 2nd try onwards

let startingToastId = null;
function showStartingBanner() {
  if (startingToastId != null) return;
  startingToastId = toast("The portal is starting. Please wait a few seconds.", {
    duration: 15000, id: "portal-starting",
  });
}
function clearStartingBanner() {
  if (startingToastId != null) {
    toast.dismiss("portal-starting");
    startingToastId = null;
  }
}

function isTransient(err) {
  if (!err.response) return true;                  // network / CORS / DNS
  return RETRY_STATUS.has(err.response.status);    // gateway / cold-start
}

api.interceptors.response.use(
  (resp) => { clearStartingBanner(); return resp; },
  async (err) => {
    const cfg = err.config || {};
    cfg.__retryCount = cfg.__retryCount || 0;
    if (!isTransient(err) || cfg.__retryCount >= MAX_RETRIES) {
      clearStartingBanner();
      return Promise.reject(err);
    }
    cfg.__retryCount += 1;
    if (cfg.__retryCount >= NOTIFY_AFTER_ATTEMPT) showStartingBanner();
    await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * cfg.__retryCount));
    return api(cfg);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
